import { Product } from '../../../../catalog/products/product';
import { IPromotions } from '../../../../checkout/cart/promotions/promotion.interface';
import { AcquisitionContextService } from '../../../../context/child/acquisition-context.service';
import { CustomerContextService } from '../../../../context/child/customer-context.service';
import { PromoContextService } from '../../../../context/child/promo-context.service';
import { ContextInterface } from '../../../../context/context.interface';
import { ContractInterface } from '../../../../context/contract.interface';
import { DateUniversalBase } from '../../../base/class/date-universal.class';

enum ALLOWED_CONVERGENCE_CONTRACTS {
    FAI_PREMIUM = 'FAI_PREMIUM',
    FAI_PREMIUM_FNB = 'FAI_PREMIUM_FNB',
}

export class PromotionBase {
    public static STATUS: string[] = [
        'INIT_CMD',
        'ATTENTE_PAIEMENT',
        'PAIEMENT_VALIDE',
        'ADRESSE_KO',
        'CONTROLES',
        'CONTROLES_OK',
        'ENCAISSE',
        'EXPEDITION_ENCOURS',
        'ENQUETE_ENCOURS',
        'ENQUETE_CONTENTIEUX',
        'INIT_COMMANDE',
    ];
    public discount: number;

    public static getRules(promotions: IPromotions[], product: Product): IPromotions[] {
        const customerContext: CustomerContextService =
            product.context.contextService.get<CustomerContextService>(CustomerContextService);
        // eslint-disable-next-line max-len
        const acquisitionContext: AcquisitionContextService =
            product.context.contextService.get<AcquisitionContextService>(AcquisitionContextService);

        const context: ContextInterface = customerContext || acquisitionContext;
        const callUnicity = this.checkPromotionsUnicities(promotions);

        const promos: IPromotions[] = promotions
            .filter(
                (promotion: IPromotions) =>
                    // eslint-disable-next-line max-len
                    this.promotionHasProduct(product, promotion) &&
                    this.checkDatesPromo(promotion) &&
                    this.checkUnicity(product, promotion, callUnicity),
            )
            .filter((promotion: IPromotions) =>
                promotion.conditions.callOperator === true ? true : this.checkContexts(promotion, context),
            );

        const promosByPostalCode = promos.filter((promo) => promo.conditions.promo_postal_code);

        if (promos && promos.length && promosByPostalCode.length) {
            return promosByPostalCode;
        }

        return promos && promos.length > 0 ? promos : null;
    }

    protected static checkUnicity(product: Product, promotion: IPromotions, call: boolean): boolean {
        const customerContext: CustomerContextService =
            product.context.contextService.get<CustomerContextService>(CustomerContextService);

        if (!call || !customerContext || !customerContext.idPersonne) {
            return true;
        }

        const promoContext: PromoContextService =
            product.context.contextService.get<PromoContextService>(PromoContextService);
        let nbPromotionApplied = 0;
        if (promoContext.currentOrders) {
            for (const item of promoContext.currentOrders.items) {
                if ('offresAchetees' in item) {
                    for (const offer of item.offresAchetees) {
                        if (!this.STATUS.includes(offer.statut)) {
                            break;
                        }
                        for (const order of offer.elementsCommandes) {
                            if (order.type === 'REMISE_ABONNEMENT' && parseFloat(order.noOffre) === promotion.id) {
                                nbPromotionApplied = nbPromotionApplied + 1;
                                break;
                            }
                        }
                    }
                }
            }
            return promotion.unicite >= nbPromotionApplied;
        } else {
            return true;
        }
    }

    /**
     * check if a promotion is only applicable n times
     * @returns
     */
    protected static checkPromotionsUnicities(promotions: IPromotions[]): boolean {
        return promotions.some((promotion) => promotion.unicite > 0);
    }

    protected static promotionHasProduct(product: Product, promotion: IPromotions): boolean {
        return (
            promotion.produits.includes(product.gencode) ||
            (promotion.produitsVisibles && promotion.produitsVisibles.includes(product.gencode))
        );
    }

    protected static checkContexts(promotion: IPromotions, context: ContextInterface): boolean {
        if (
            context instanceof AcquisitionContextService &&
            (promotion.conditions.acquisition || promotion.conditions.client)
        ) {
            return true;
        } else if (
            context instanceof CustomerContextService &&
            context.contracts.length === 0 &&
            promotion.conditions.acquisition
        ) {
            return true;
        } else if (context instanceof CustomerContextService && promotion.conditions.client) {
            return (
                this.hasContractType(promotion.conditions.client.contrats, context.contracts) &&
                this.checkStatut(promotion.conditions.client.statut, context.statut)
            );
        }
        return false;
    }

    protected static checkStatut(statutsPromo: string[], clienStatut?: string[]): boolean {
        return statutsPromo.includes('ALL') || (clienStatut && statutsPromo.includes(clienStatut[0]));
    }

    protected static checkDatesPromo(dates: IPromotions) {
        if (dates.debut && dates.fin) {
            const now = new DateUniversalBase('now');
            const start = new DateUniversalBase(dates.debut);
            const end = new DateUniversalBase(dates.fin);
            return DateUniversalBase.dateBetween(start, end, now);
        } else {
            return true;
        }
    }

    public static hasContractType(promoContracts: string[], clienContrats: ContractInterface[]): boolean {
        if ((promoContracts && promoContracts.includes('ALL')) || !clienContrats.length) {
            return true;
        }
        const activeContractTypes = {
            FNB: false,
            FAI: false,
            FAI_PREMIUM: false,
        };
        const contracts: ContractInterface[] = clienContrats;
        for (const contract of contracts) {
            if (contract.ligneMarche === 'FNB' && contract.abonnement.offreTokyo && contract.abonnement.offrePremium) {
                activeContractTypes.FAI_PREMIUM = true;
            } else if (
                contract.ligneMarche === 'FNB' &&
                contract.abonnement.offreTokyo &&
                contract.abonnement.offrePremium === false
            ) {
                activeContractTypes.FAI = true;
            } else if (contract.ligneMarche === 'FAI' && contract.abonnement.offrePremium) {
                activeContractTypes.FAI_PREMIUM = true;
            } else {
                activeContractTypes[contract.ligneMarche] = true;
            }
        }
        let aggregatedContractType: string = '';
        if (activeContractTypes.FAI_PREMIUM && activeContractTypes.FNB) {
            aggregatedContractType = 'FAI_PREMIUM_FNB';
        } else if (activeContractTypes.FAI && activeContractTypes.FNB) {
            aggregatedContractType = 'FAI_FNB';
        } else if (activeContractTypes.FAI_PREMIUM) {
            aggregatedContractType = 'FAI_PREMIUM';
        } else if (activeContractTypes.FAI) {
            aggregatedContractType = 'FAI';
        } else if (activeContractTypes.FNB) {
            aggregatedContractType = 'FNB';
        }

        if (promoContracts) {
            return promoContracts.includes(aggregatedContractType);
        } else {
            return Object.keys(ALLOWED_CONVERGENCE_CONTRACTS).includes(aggregatedContractType);
        }
    }
}
